import { useQuery } from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns/minutesToMilliseconds";
import type { BusinessConfigTenant } from "~contexts/BusinessConfigContext/BusinessConfigContext";
import { useBusinessConfig } from "~contexts/BusinessConfigContext/BusinessConfigContext";
import type { Tenant as ApiTenant } from "~generated";
import { TenantsService } from "~generated";
import type { QueryOpts } from "./helpers";

export interface Tenant extends ApiTenant {
  shortName: string;
  abbreviation: string;
}

function getTenantShortName(
  tenantNumber: Tenant["number"],
  businessConfigTenants: BusinessConfigTenant[],
) {
  return (
    businessConfigTenants.find((t) => t.tenant_number === tenantNumber)
      ?.short_name || "unbekannter Mandant"
  );
}

function getTenantAbbreviation(
  tenantNumber: Tenant["number"],
  businessConfigTenants: BusinessConfigTenant[],
) {
  return (
    businessConfigTenants.find((t) => t.tenant_number === tenantNumber)
      ?.abbreviation || "unbekannter Mandant"
  );
}

export const tenantKeys = {
  base: () => ["tenants"] as const, // no version because it is root of most queries
  list: () => [...tenantKeys.base(), "list"] as const,
  detail: (tenantId: string) =>
    [...tenantKeys.base(), "detail", tenantId] as const,
  me: () => [...tenantKeys.base(), "me"] as const,
};

export function useCurrentEmployeeTenants() {
  const { tenants: businessConfigTenants } = useBusinessConfig();
  return useQuery({
    queryKey: tenantKeys.me(),
    queryFn: async () => {
      const tenants = await TenantsService.getTenants();
      if (!tenants?.tenants) {
        return [];
      }
      return tenants.tenants.map((tenant) => ({
        shortName: getTenantShortName(tenant.number, businessConfigTenants),
        abbreviation: getTenantAbbreviation(
          tenant.number,
          businessConfigTenants,
        ),
        ...tenant,
      }));
    },
    // Gate, header and route redirects each mount an observer; the first response serves them all
    staleTime: minutesToMilliseconds(1),
    // this query gates the whole app: refetching on focus is what recovers it after a failure
    refetchOnWindowFocus: true,
  });
}

export type TenantGroup = ReturnType<typeof useTenantGroups>[number];

export function useTenantGroups() {
  const { tenantGroups } = useBusinessConfig();
  return tenantGroups;
}

export function useAllTenants(
  params: { includeInactive?: boolean } = { includeInactive: false },
  options?: QueryOpts<Tenant[], ReturnType<typeof tenantKeys.list>>,
) {
  const includeInactive = params.includeInactive ?? false;
  const { tenants: businessConfigTenants } = useBusinessConfig();
  return useQuery({
    ...options,
    queryKey: tenantKeys.list(),
    queryFn: async () => {
      const tenants = await TenantsService.getAllTenants(true);
      if (!tenants?.tenants) {
        return [];
      }
      return tenants.tenants.map((tenant) => ({
        shortName: getTenantShortName(tenant.number, businessConfigTenants),
        abbreviation: getTenantAbbreviation(
          tenant.number,
          businessConfigTenants,
        ),
        ...tenant,
      }));
    },
    select: (tenants) =>
      includeInactive ? tenants : tenants.filter((tenant) => tenant.active),
    // Tenants are added/deactivated only a handful of times a year: a long
    // staleTime keeps callers that enable at different times (e.g. a query
    // gated on another one's result) from triggering a second request.
    staleTime: options?.staleTime ?? minutesToMilliseconds(5),
  });
}
